<script>
import { useUserStore } from "../../../stores/user";
import {useRouter} from "vue-router";

export default {
  setup() {
    const userStore = useUserStore();

    const router = useRouter();

    const toggleEdit = () => {
      if (userStore.editSettings) {
        userStore.saveSettings();
      } else {
        userStore.toggleEditSettings();
      }
    };

    const cancelEditSettings = () => {
      userStore.cancelEditSettings();
    };

    // Validation function
    const validateForm = () => {
      return userStore.validateForm();
    };

    return {
      userStore,
      toggleEdit,
      cancelEditSettings,
      validateForm,
      router
    };
  },
  computed: {
    user() {
      return this.userStore.user;
    },
    localUser() {
      return this.userStore.localUser;
    },
    editSettings() {
      return this.userStore.editSettings;
    },
    triggerSignOut() {
      return this.userStore.triggerSignOut;
    }
  },
  watch: {
    user() {
      // Set initial values from userStore
      this.localUser = { ...this.user };
    },
    triggerSignOut() {
      window.location.href = "/users/sign_in";
    }
  }
};
</script>

<template>
  <div class="w-full p-4 pt-2">
    <div class="bg-white rounded-xl shadow-lg p-6">
      <p class="pb-0 font-bold">
        Personal Settings
      </p>
    </div>
  </div>
  <div class="w-full p-4">
    <div class="bg-white rounded-xl shadow-lg p-6">
      <table class="w-full text-left">
        <!-- Account Section -->
        <thead>
          <tr>
            <th class="pb-2 font-bold">
              Account
            </th>
          </tr>
          <tr>
            <td class="pb-4 text-xs text-gray-600 border-b">
              Real-time information about your profile
            </td>
          </tr>
        </thead>

        <!-- Full Name Section -->
        <tbody>
          <tr>
            <th class="pt-5 font-bold">
              Full Name
            </th>
          </tr>
          <tr>
            <td
              v-if="editSettings"
              class="pt-2 pb-2 text-xs text-gray-600"
            >
              Change your profile first name and last name
            </td>
          </tr>
          <tr class="border-b">
            <td class="pt-2 pb-2">
              <div class="flex justify-start space-x-24">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    First Name
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ user.first_name }}
                    </p>
                    <input
                      v-else
                      v-model="localUser.first_name"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="userStore.errors.first_name"
                    class="text-red-500 text-xs"
                  >{{ userStore.errors.first_name }}</span>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Last Name
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ user.last_name }}
                    </p>
                    <input
                      v-else
                      v-model="localUser.last_name"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="userStore.errors.last_name"
                    class="text-red-500 text-xs"
                  >{{ userStore.errors.last_name }}</span>
                </div>
              </div>
            </td>
          </tr>

          <!-- Password Section -->
          <tr>
            <th class="pt-5 pb-2 font-bold">
              Password
            </th>
          </tr>
          <tr>
            <td>
              <div v-if="!editSettings">
                <p class="pb-4 text-xs text-gray-600">
                  You can change your password while in edit mode
                </p>
              </div>
              <div v-else>
                <p class="pb-4 text-xs text-gray-600">
                  Changing your account password will require you to sign back in
                </p>
                <div class="flex justify-start space-x-24">
                  <div class="w-1/2">
                    <p class="pb-2 font-bold text-xs text-gray-600">
                      New Password
                    </p>
                    <input
                      v-model="userStore.newPassword"
                      type="password"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1"
                    >
                    <span
                      v-if="userStore.errors.new_password"
                      class="text-red-500 text-xs"
                    >{{ userStore.errors.new_password }}</span>
                  </div>
                  <div class="w-1/2">
                    <p class="pb-2 font-bold text-xs text-gray-600">
                      Confirm New Password
                    </p>
                    <input
                      v-model="userStore.confirmPassword"
                      type="password"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1"
                    >
                    <span
                      v-if="userStore.errors.confirm_password"
                      class="text-red-500 text-xs"
                    >{{ userStore.errors.confirm_password }}</span>
                  </div>
                </div>
              </div>
            </td>
          </tr>

          <!-- Buttons Section -->
          <tr>
            <th class="pt-4">
              <div class="flex space-x-2">
                <button
                  v-if="editSettings"
                  class="btn-outline"
                  @click="cancelEditSettings"
                >
                  Cancel
                </button>
                <button
                  v-if="editSettings"
                  class="btn-outline"
                  @click="userStore.changePassword"
                >
                  Change Password
                </button>
                <button
                  :class="editSettings ? 'btn-filled' : 'btn-outline'"
                  @click="toggleEdit"
                >
                  {{ editSettings ? 'Save' : 'Edit' }}
                </button>
              </div>
            </th>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped lang="scss">
  th {
    width: 25%;
    font-size: 10px;
    font-weight: normal;
    text-align: left;
    text-transform: uppercase;
    @apply text-slate-500;
    @apply px-2;
    //@apply py-2;
  }

  td {
    width: 25%;
    font-size: 12px;
    @apply px-2;
    //@apply py-4;
  }

  .remove-button {
    @apply text-white;
    background-color: #256662;
    border-radius: 5px;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: 1px solid #256662;
    cursor: pointer;
  }

  .relative {
    position: relative;
  }

  .absolute {
    position: absolute;
  }

  input {
    height: 30px;
  }

  p.absolute {
    line-height: 30px;
  }

  @media screen and (max-width: 720px) {
    .flex {
      flex-direction: row;
      align-items: center;
    }
  }
</style>
